import React from 'react';
import { graphql } from 'gatsby';
import { Col, Row } from 'components/Core/Grid';
import { SectionLayout } from 'components/Sections/SectionLayout';
import CallToActionCards from 'components/Sections/CallToActionCards';
import SidebarV2 from 'components/Sections/Sidebar/SidebarV2';
import StyledRichText from 'components/RichText/StyledRichText';
import WebsiteLayout from 'layouts/websiteLayout';
import SEO, { createSEO } from 'components/Core/SEO';
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import dayJs from 'utils/date';
import NotFound from '../../pages/404';

const queryClient = new QueryClient();

const QueryWrapper = (WrappedComponent) => (props) =>
  (
    <QueryClientProvider client={queryClient}>
      <WrappedComponent {...props} />
    </QueryClientProvider>
  );

const PressDetailsTemplate = (props) => {
  const { edges } = props.data.allContentfulPress;
  const data = props.data.contentfulPress;

  const { data: clientData, status } =
    !data &&
    useQuery({
      queryKey: ['pressDetails'],
      queryFn: async () => {
        const res = await fetch(`${process.env.GATSBY_API_URL}${props.location.pathname}`);
        const d = await res.json();

        return d.data.pressCollection.items
          .map((item) => ({
            ...item,
            seo: {
              ...item.seo,
              metaDescription: {
                metaDescription: item.seo.metaDescription,
              },
            },
            content: {
              raw: JSON.stringify(item.content.json),
            },
          }))
          .shift();
      },
    });

  const { postTitle, content, postDate, id, seo: pressSeo } = data || clientData || {};

  if (status && status !== 'pending' && !clientData) {
    return <NotFound {...props} />;
  }

  if (status && status !== 'success') {
    return <></>;
  }

  const seo = createSEO({ ...pressSeo }, props);

  return (
    <WebsiteLayout layout="industry">
      <SEO {...seo} />
      <SectionLayout backgroundColor="white" contentBelowSection>
        <div>
          <Row className="section-press-details-titles" type="flex">
            <Col lg={16} md={16} sm={16} xs={24}>
              <h1 className="text-darkest mb-5">{postTitle}</h1>
              <div className="mb-[30px] xl-max:mb-0">
                <span className="uppercase text-grey-8 text-[13px] tracking-[-0.05em] mr-5">
                  {dayJs(postDate).format('MMMM DD, YYYY')}
                </span>
              </div>
            </Col>
          </Row>
          <Row className="section-press-details-contents" gutter={{ md: 40, sm: 40 }} type="flex">
            <Col lg={16} md={15} sm={24} xs={24}>
              <StyledRichText content={content} className="xl-max:mt-5" />
            </Col>
            <Col lg={8} md={9} sm={24} xs={24}>
              <SidebarV2 email demo />
            </Col>
          </Row>
        </div>
      </SectionLayout>
      <CallToActionCards
        title="Related posts"
        backgroundColor="White"
        cardType="Press"
        cardInfo={edges.filter((x) => x.node.id !== id).slice(0, 3)}
        cta={{ buttonLabel: 'See more posts', color: 'Orange', url: '/press' }}
      />
    </WebsiteLayout>
  );
};

export default QueryWrapper(PressDetailsTemplate);

export const PressDetailsTemplateQuery = graphql`
  query PressPostBySlug($slug: String!) {
    contentfulPress(seo: { pageUrl: { eq: $slug } }) {
      __typename
      updatedAt
      id
      seo {
        ...Seo
      }
      postTitle
      content {
        raw
      }
      postDate(formatString: "MMMM DD, YYYY")
    }
    allContentfulPress(sort: { createdAt: DESC }, limit: 4) {
      edges {
        node {
          __typename
          updatedAt
          id
          postTitle
          seo {
            ...Seo
          }
          postDate(formatString: "MMMM DD, YYYY")
        }
      }
    }
    allContentfulImage(
      filter: {
        contentful_id: {
          in: [
            "4JPMY7Ky3OHOm8bMZCL0hh" # 404-wrench
          ]
        }
      }
    ) {
      edges {
        node {
          internalName
          image {
            file {
              url
            }
          }
        }
      }
    }
  }
`;
